import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'list', 'quota', 'submit', 'error'];
  static values = {
    maxFiles: Number,
    maxMb: Number,
    maxCombinedMb: Number,
    processableExt: { type: String, default: '.csv,.xlsx,.xls' },
    unsupported: String,
    skipped: String,
    quota: String,
  };

  connect() {
    this.processableExts = this.processableExtValue
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
    this.refresh();
  }

  changed() {
    this.dropInvalidFiles();
    this.refresh();
  }

  remove(event) {
    const index = Number(event.currentTarget.dataset.index);
    this.keepFiles((_, i) => i !== index);
    this.refresh();
  }

  guard(event) {
    this.keepFiles((file) => this.isProcessable(file));
    if (this.batchInvalid() || this.inputTarget.files.length === 0) event.preventDefault();
  }

  dropInvalidFiles() {
    const maxBytes = this.maxMbValue * 1024 * 1024;
    const rejected = [];

    this.keepFiles((file) => {
      if (file.size > maxBytes) {
        rejected.push(file.name);
        return false;
      }
      return true;
    });

    this.showRejected(rejected);
  }

  refresh() {
    const files = Array.from(this.inputTarget.files || []);
    const combined = files.reduce((sum, file) => sum + file.size, 0);
    const combinedMb = combined / (1024 * 1024);
    const overLimit = this.batchInvalid(files, combined);

    this.renderList(files);
    this.updateQuota(files.length, combinedMb, overLimit);
    if (this.hasSubmitTarget) {
      const processableCount = files.filter((file) => this.isProcessable(file)).length;
      this.submitTarget.disabled = overLimit || processableCount === 0;
    }
  }

  batchInvalid(
    files = Array.from(this.inputTarget.files || []),
    combined = files.reduce((sum, file) => sum + file.size, 0)
  ) {
    return files.length > this.maxFilesValue || combined > this.maxCombinedMbValue * 1024 * 1024;
  }

  renderList(files) {
    if (!this.hasListTarget) return;

    this.listTarget.replaceChildren();
    files.forEach((file, index) => {
      const processable = this.isProcessable(file);
      const item = document.createElement('li');
      item.className = processable
        ? 'flex items-center justify-between gap-2 text-sm bg-confirmationLight rounded-md px-3 py-2'
        : 'flex items-center justify-between gap-2 text-sm bg-errorLight rounded-md px-3 py-2';

      const label = document.createElement('span');
      label.className = 'flex flex-col min-w-0';
      const name = document.createElement('span');
      name.textContent = `${file.name} (${this.formatSize(file.size)})`;
      label.append(name);
      if (!processable) {
        const note = document.createElement('span');
        note.className = 'text-xs text-alertRed';
        note.textContent = this.unsupportedValue;
        label.append(note);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.action = 'manual-upload-files#remove';
      remove.dataset.index = String(index);
      remove.className = 'text-grayText hover:text-alertRed text-lg leading-none shrink-0';
      remove.setAttribute('aria-label', 'Remove');
      remove.textContent = '×';

      item.append(label, remove);
      this.listTarget.append(item);
    });
  }

  updateQuota(count, combinedMb, overLimit) {
    if (!this.hasQuotaTarget) return;

    this.quotaTarget.textContent = this.quotaValue
      .replace('%{count}', String(count))
      .replace('%{max}', String(this.maxFilesValue))
      .replace('%{size_mb}', combinedMb.toFixed(1))
      .replace('%{max_combined_mb}', String(this.maxCombinedMbValue));
    this.quotaTarget.classList.toggle('text-alertRed', overLimit);
    this.quotaTarget.classList.toggle('text-grayText', !overLimit);
  }

  showRejected(names) {
    if (!this.hasErrorTarget) return;

    if (!names.length) {
      this.errorTarget.textContent = '';
      this.errorTarget.classList.add('hidden');
      return;
    }

    this.errorTarget.textContent = this.skippedValue.replace('%{names}', names.join(', '));
    this.errorTarget.classList.remove('hidden');
  }

  keepFiles(predicate) {
    const kept = new DataTransfer();
    Array.from(this.inputTarget.files || []).forEach((file, index) => {
      if (predicate(file, index)) kept.items.add(file);
    });
    this.inputTarget.files = kept.files;
  }

  formatSize(bytes) {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  isProcessable(file) {
    const ext = `.${file.name.split('.').pop()}`.toLowerCase();
    return this.processableExts.includes(ext);
  }
}
